import React, { useState, useEffect } from 'react';
import { FiClock, FiCheckCircle, FiAlertCircle, FiPlay, FiFileText, FiArrowLeft, FiMail } from 'react-icons/fi';
import { studentAPI } from '../../services/api';
import './StudentDashboard.css';
import './StudentTestsBlocksPage.css';

const StudentTestsBlocksPage = () => {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTest, setSelectedTest] = useState(null);
    const [testData, setTestData] = useState(null);
    const [blockAnswers, setBlockAnswers] = useState({});
    const [startTime, setStartTime] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
    const [showDetailedResults, setShowDetailedResults] = useState(false);
    const [detailedResults, setDetailedResults] = useState(null);

    useEffect(() => {
        loadTests();
    }, []);

    useEffect(() => {
        if (timeLeft === null || timeLeft <= 0) return;
        
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    handleSubmitTest();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const loadTests = async () => {
        try {
            setLoading(true);
            const response = await studentAPI.getAvailableTests();
            setTests(response.data.tests || []);
        } catch (error) {
            console.error('Ошибка загрузки тестов:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartTest = async (test) => {
        try {
            const response = await studentAPI.startTest(test.id);
            console.log('Test data received:', response.data); // Отладка
            setTestData(response.data);
            setSelectedTest(test);
            setBlockAnswers({});
            setStartTime(new Date());
            setCurrentBlockIndex(0);
            
            if (test.time_limit > 0) {
                setTimeLeft(test.time_limit * 60);
            }
        } catch (error) {
            console.error('Ошибка загрузки теста:', error);
            alert('Ошибка при загрузке теста');
        }
    };

    const handleViewDetailedResults = async (resultId) => {
        try {
            const response = await studentAPI.getTestResultDetails(resultId);
            setDetailedResults(response.data);
            setShowDetailedResults(true);
        } catch (error) {
            console.error('Ошибка загрузки детальных результатов:', error);
            alert('Ошибка при загрузке результатов');
        }
    };

    const handleBlockAnswerChange = (blockId, questionId, answer) => {
        setBlockAnswers(prev => ({
            ...prev,
            [blockId]: {
                ...prev[blockId],
                questions: {
                    ...(prev[blockId]?.questions || {}),
                    [questionId]: answer
                }
            }
        }));
    };

    const isMcOptionSelected = (currentAnswer, value) => {
        if (Array.isArray(currentAnswer)) return currentAnswer.includes(value);
        return currentAnswer === value;
    };

    const toggleMcAnswer = (blockId, questionId, value, allowMultiple) => {
        if (!allowMultiple) {
            handleBlockAnswerChange(blockId, questionId, value);
            return;
        }
        setBlockAnswers((prev) => {
            const current = prev[blockId]?.questions?.[questionId];
            let next = Array.isArray(current)
                ? [...current]
                : (current != null && current !== '' ? [current] : []);
            if (next.includes(value)) {
                next = next.filter((v) => v !== value);
            } else {
                next.push(value);
            }
            next.sort((a, b) => {
                if (typeof a === 'number' && typeof b === 'number') return a - b;
                return String(a).localeCompare(String(b));
            });
            return {
                ...prev,
                [blockId]: {
                    ...prev[blockId],
                    questions: {
                        ...(prev[blockId]?.questions || {}),
                        [questionId]: next,
                    },
                },
            };
        });
    };

    const handleWritingChange = (blockId, text) => {
        setBlockAnswers(prev => ({
            ...prev,
            [blockId]: {
                ...prev[blockId],
                text
            }
        }));
    };

    const handleSubmitTest = async () => {
        if (!window.confirm('Вы уверены, что хотите отправить тест?')) {
            return;
        }

        try {
            const endTime = new Date();
            const timeSpent = Math.floor((endTime - startTime) / 1000);
            
            const response = await studentAPI.submitTest(selectedTest.id, {
                blockAnswers,
                time_spent: timeSpent,
                started_at: startTime
            });
            
            setTestResults(response.data);
            setShowResults(true);
        } catch (error) {
            console.error('Ошибка отправки теста:', error);
            alert('Ошибка при отправке теста');
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            beginner: '#52c41a',
            elementary: '#73d13d',
            'pre-intermediate': '#faad14',
            intermediate: '#fa8c16',
            'upper-intermediate': '#f5222d',
            advanced: '#cf1322'
        };
        return colors[difficulty] || '#999';
    };

    const getBlockName = (type) => {
        const names = {
            grammar: 'Грамматика',
            listening: 'Аудирование',
            reading: 'Чтение',
            writing: 'Письмо'
        };
        return names[type] || type;
    };

    const renderBlock = (block, blockIndex) => {
        if (block.block_type === 'grammar') {
            return renderGrammarBlock(block);
        } else if (block.block_type === 'listening') {
            return renderListeningBlock(block);
        } else if (block.block_type === 'reading') {
            return renderReadingBlock(block);
        } else if (block.block_type === 'writing') {
            return renderWritingBlock(block);
        }
    };

    const renderGrammarBlock = (block) => {
        return (
            <div>
                {(block.exercises || []).map((exercise, exIndex) => (
                    <div key={exIndex} style={{ marginBottom: '30px' }}>
                        <h3>{exercise.title}</h3>
                        {exercise.instructions && <p style={{ color: '#666' }}>{exercise.instructions}</p>}
                        
                        {(exercise.questions || []).map((question, qIndex) => {
                            const questionData = typeof question.question_data === 'string' 
                                ? JSON.parse(question.question_data) 
                                : question.question_data;
                            
                            return (
                                <div key={question.id} className="card" style={{ padding: '20px', marginBottom: '15px' }}>
                                    <div style={{ marginBottom: '15px', fontWeight: '500' }}>
                                        {qIndex + 1}. {question.question_text}
                                    </div>
                                    
                                    {exercise.exercise_type === 'multiple_choice' && (() => {
                                        const allowMultiple = !!questionData?.allowMultipleCorrect;
                                        const currentAnswer = blockAnswers[block.id]?.questions?.[question.id];
                                        return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {allowMultiple && (
                                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                                                    Выберите все подходящие варианты
                                                </div>
                                            )}
                                            {(questionData.options || []).map((option, optIndex) => (
                                                <label key={optIndex} style={{
                                                    padding: '12px',
                                                    border: `2px solid ${isMcOptionSelected(currentAnswer, optIndex) ? '#4a90e2' : '#e0e0e0'}`,
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    background: isMcOptionSelected(currentAnswer, optIndex) ? '#e6f7ff' : 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px'
                                                }}>
                                                    <input
                                                        type={allowMultiple ? 'checkbox' : 'radio'}
                                                        name={`q-${question.id}`}
                                                        checked={isMcOptionSelected(currentAnswer, optIndex)}
                                                        onChange={() => toggleMcAnswer(block.id, question.id, optIndex, allowMultiple)}
                                                    />
                                                    <span>{option}</span>
                                                </label>
                                            ))}
                                        </div>
                                        );
                                    })()}
                                    
                                    {exercise.exercise_type === 'true_false' && (
                                        <div style={{ display: 'flex', gap: '15px' }}>
                                            {[true, false].map(value => (
                                                <label key={String(value)} style={{
                                                    flex: 1,
                                                    padding: '12px',
                                                    border: `2px solid ${blockAnswers[block.id]?.questions?.[question.id] === value ? '#4a90e2' : '#e0e0e0'}`,
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    background: blockAnswers[block.id]?.questions?.[question.id] === value ? '#e6f7ff' : 'white',
                                                    textAlign: 'center',
                                                    fontWeight: '500'
                                                }}>
                                                    <input
                                                        type="radio"
                                                        name={`q-${question.id}`}
                                                        checked={blockAnswers[block.id]?.questions?.[question.id] === value}
                                                        onChange={() => handleBlockAnswerChange(block.id, question.id, value)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {value ? 'True (Правда)' : 'False (Ложь)'}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {exercise.exercise_type === 'fill_gaps' && (
                                        <div>
                                            <div style={{ marginBottom: '15px', lineHeight: '2' }}>
                                                {(questionData.sentence || '').split('___').map((part, i, arr) => (
                                                    <React.Fragment key={i}>
                                                        {part}
                                                        {i < arr.length - 1 && (
                                                            <input
                                                                type="text"
                                                                value={blockAnswers[block.id]?.questions?.[question.id]?.[i] || ''}
                                                                onChange={(e) => {
                                                                    const answers = [...(blockAnswers[block.id]?.questions?.[question.id] || [])];
                                                                    answers[i] = e.target.value;
                                                                    handleBlockAnswerChange(block.id, question.id, answers);
                                                                }}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px',
                                                                    width: '120px',
                                                                    margin: '0 5px'
                                                                }}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {exercise.exercise_type === 'text_answer' && (
                                        <textarea
                                            value={blockAnswers[block.id]?.questions?.[question.id] || ''}
                                            onChange={(e) => handleBlockAnswerChange(block.id, question.id, e.target.value)}
                                            rows={5}
                                            placeholder="Введите ответ…"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '15px',
                                                fontFamily: 'inherit',
                                                resize: 'vertical'
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    const renderListeningBlock = (block) => {
        console.log('Rendering listening block:', block); // Отладка
        console.log('Listening questions:', block.questions); // Отладка
        
        return (
            <div>
                {block.listeningBlock?.description && (
                    <div className="card" style={{ padding: '20px', marginBottom: '20px', background: '#e6f7ff' }}>
                        <p style={{ margin: 0 }}>{block.listeningBlock.description}</p>
                    </div>
                )}
                
                {block.listeningBlock?.audio_url && (
                    <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
                        <audio controls style={{ width: '100%' }}>
                            <source src={block.listeningBlock.audio_url} type="audio/mpeg" />
                        </audio>
                    </div>
                )}
                
                <div style={{ marginBottom: '20px', padding: '15px', background: '#fff7e6', borderRadius: '8px' }}>
                    <strong>Вопросов загружено: {block.questions?.length || 0}</strong>
                </div>
                
                {(block.questions || []).map((question, index) => (
                    <div key={question.id} className="card" style={{ padding: '20px', marginBottom: '15px' }}>
                        <div style={{ marginBottom: '15px', fontWeight: '500' }}>
                            {index + 1}. {question.question_text}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {['true', 'false', 'not_given'].map(value => (
                                <label key={value} style={{
                                    flex: 1,
                                    padding: '12px',
                                    border: `2px solid ${blockAnswers[block.id]?.questions?.[question.id] === value ? '#4a90e2' : '#e0e0e0'}`,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    background: blockAnswers[block.id]?.questions?.[question.id] === value ? '#e6f7ff' : 'white',
                                    textAlign: 'center',
                                    fontWeight: '500'
                                }}>
                                    <input
                                        type="radio"
                                        name={`q-${question.id}`}
                                        checked={blockAnswers[block.id]?.questions?.[question.id] === value}
                                        onChange={() => handleBlockAnswerChange(block.id, question.id, value)}
                                        style={{ display: 'none' }}
                                    />
                                    {value === 'true' ? 'True' : value === 'false' ? 'False' : 'Not Given'}
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderReadingBlock = (block) => {
        console.log('Rendering reading block:', block); // Отладка
        console.log('Reading questions:', block.questions); // Отладка
        
        return (
            <div>
                {block.readingBlock?.reading_text && (
                    <div className="card" style={{ padding: '25px', marginBottom: '25px', background: '#fffbe6' }}>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '16px' }}>
                            {block.readingBlock.reading_text}
                        </div>
                    </div>
                )}
                
                <div style={{ marginBottom: '20px', padding: '15px', background: '#fff7e6', borderRadius: '8px' }}>
                    <strong>Вопросов загружено: {block.questions?.length || 0}</strong>
                </div>
                
                {(block.questions || []).map((question, index) => {
                    const options = typeof question.options === 'string' 
                        ? JSON.parse(question.options) 
                        : question.options;
                    
                    return (
                        <div key={question.id} className="card" style={{ padding: '20px', marginBottom: '15px' }}>
                            <div style={{ marginBottom: '15px', fontWeight: '500' }}>
                                {index + 1}. {question.question_text}
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {question.allowMultipleCorrect && (
                                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                                        Выберите все подходящие варианты
                                    </div>
                                )}
                                {(options || []).map((option, optIndex) => {
                                    const currentAnswer = blockAnswers[block.id]?.questions?.[question.id];
                                    return (
                                    <label key={optIndex} style={{
                                        padding: '12px',
                                        border: `2px solid ${isMcOptionSelected(currentAnswer, option) ? '#4a90e2' : '#e0e0e0'}`,
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        background: isMcOptionSelected(currentAnswer, option) ? '#e6f7ff' : 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}>
                                        <input
                                            type={question.allowMultipleCorrect ? 'checkbox' : 'radio'}
                                            name={`q-${question.id}`}
                                            checked={isMcOptionSelected(currentAnswer, option)}
                                            onChange={() => toggleMcAnswer(block.id, question.id, option, !!question.allowMultipleCorrect)}
                                        />
                                        <span>{option}</span>
                                    </label>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderWritingBlock = (block) => {
        const writingBlock = block.writingBlock;
        const currentText = blockAnswers[block.id]?.text || '';
        const wordCount = currentText.trim() ? currentText.trim().split(/\s+/).length : 0;
        
        return (
            <div>
                <div className="card" style={{ padding: '20px', marginBottom: '20px', background: '#fff7e6' }}>
                    <div style={{ whiteSpace: 'pre-wrap', marginBottom: '15px' }}>
                        {writingBlock.task_description}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                        <strong>Требования:</strong>
                        <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                            <li>Минимум слов: {writingBlock.min_words}</li>
                            <li>Максимум слов: {writingBlock.max_words}</li>
                            <li>Максимальный балл: {writingBlock.max_points}</li>
                        </ul>
                    </div>
                </div>
                
                <div className="card" style={{ padding: '20px' }}>
                    <textarea
                        value={currentText}
                        onChange={(e) => handleWritingChange(block.id, e.target.value)}
                        rows={12}
                        placeholder="Напишите ваш текст здесь..."
                        style={{
                            width: '100%',
                            padding: '15px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '16px',
                            lineHeight: '1.6',
                            fontFamily: 'inherit'
                        }}
                    />
                    <div style={{ 
                        marginTop: '10px', 
                        fontSize: '14px',
                        color: wordCount < writingBlock.min_words ? '#ff4d4f' : wordCount > writingBlock.max_words ? '#faad14' : '#52c41a'
                    }}>
                        Количество слов: {wordCount} 
                        {wordCount < writingBlock.min_words && ` (минимум ${writingBlock.min_words})`}
                        {wordCount > writingBlock.max_words && ` (максимум ${writingBlock.max_words})`}
                    </div>
                </div>
            </div>
        );
    };

    // Результаты теста
    if (showResults && testResults) {
        return (
            <main className="student-content">
                <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
                        {testResults.hasWriting && !testResults.writingChecked ? (
                            // Если есть письмо И оно НЕ проверено - показываем "ожидает проверки"
                            <>
                                <div style={{
                                    width: '120px',
                                    height: '120px',
                                    borderRadius: '50%',
                                    background: '#faad14',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 30px',
                                    fontSize: '48px'
                                }}>
                                    <FiClock size={60} />
                                </div>

                                <h1 style={{ margin: '0 0 10px 0' }}>
                                    Тест отправлен на проверку!
                                </h1>
                                
                                <p style={{ fontSize: '18px', color: '#666', marginBottom: '30px' }}>
                                    Ваша работа отправлена преподавателю. Итоговый результат будет доступен после проверки письменной части.
                                </p>
                                
                                <div style={{ 
                                    padding: '20px', 
                                    background: '#fff7e6', 
                                    borderRadius: '8px',
                                    border: '1px solid #ffd591',
                                    marginBottom: '30px'
                                }}>
                                    <p style={{ margin: 0, color: '#d48806', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                        <FiMail /> Вы получите уведомление, когда преподаватель проверит вашу работу
                                    </p>
                                </div>
                            </>
                        ) : (
                            // Если нет письма ИЛИ письмо проверено - показываем результат
                            <>
                                <div style={{
                                    width: '120px',
                                    height: '120px',
                                    borderRadius: '50%',
                                    background: testResults.passed ? '#52c41a' : '#ff4d4f',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 30px',
                                    fontSize: '48px',
                                    fontWeight: 'bold'
                                }}>
                                    {Math.round(testResults.percentage)}%
                                </div>

                                <h1 style={{ margin: '0 0 10px 0' }}>
                                    {testResults.passed ? 'Тест пройден!' : 'Тест не пройден'}
                                </h1>
                                
                                <p style={{ fontSize: '18px', color: '#666', marginBottom: '30px' }}>
                                    {testResults.passed 
                                        ? `Поздравляем! Вы успешно прошли тест "${selectedTest.title}"`
                                        : `К сожалению, вы не набрали проходной балл для теста "${selectedTest.title}"`
                                    }
                                </p>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '30px' }}>
                                    <div>
                                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4a90e2' }}>
                                            {testResults.score}
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#999' }}>Набрано баллов</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#999' }}>
                                            {testResults.maxScore}
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#999' }}>Максимум баллов</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: testResults.passed ? '#52c41a' : '#ff4d4f' }}>
                                            {Math.round(testResults.percentage)}%
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#999' }}>Процент</div>
                                    </div>
                                </div>
                            </>
                        )}

                        <button
                            onClick={() => {
                                setSelectedTest(null);
                                setShowResults(false);
                                setTestResults(null);
                                loadTests();
                            }}
                            style={{
                                padding: '12px 30px',
                                background: '#4a90e2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Вернуться к тестам
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    // Прохождение теста
    if (selectedTest && testData) {
        const currentBlock = testData.blocks[currentBlockIndex];
        
        return (
            <main className="student-content">
                <div className="container">
                    {/* Хедер теста */}
                    <div className="card" style={{ marginBottom: '20px', padding: '20px', position: 'sticky', top: '90px', zIndex: 50, background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ margin: '0 0 5px 0' }}>{selectedTest.title}</h2>
                                <div style={{ display: 'flex', gap: '15px', fontSize: '14px', color: '#666' }}>
                                    <span>Блок {currentBlockIndex + 1} из {testData.blocks.length}: {getBlockName(currentBlock.block_type)}</span>
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                {timeLeft !== null && (
                                    <div style={{
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        color: timeLeft < 300 ? '#f5222d' : '#4a90e2',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}>
                                        <FiClock />
                                        {formatTime(timeLeft)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Навигация по блокам */}
                    <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', overflowX: 'auto' }}>
                        {testData.blocks.map((block, index) => (
                            <button
                                key={block.id}
                                onClick={() => setCurrentBlockIndex(index)}
                                style={{
                                    padding: '10px 20px',
                                    background: index === currentBlockIndex ? '#4a90e2' : '#f0f0f0',
                                    color: index === currentBlockIndex ? 'white' : '#666',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {getBlockName(block.block_type)}
                            </button>
                        ))}
                    </div>

                    {/* Текущий блок */}
                    <div style={{ marginBottom: '20px' }}>
                        <h2>{currentBlock.title}</h2>
                        {renderBlock(currentBlock, currentBlockIndex)}
                    </div>

                    {/* Кнопки навигации */}
                    <div className="card" style={{ padding: '20px', display: 'flex', justifyContent: 'space-between' }}>
                        <button
                            onClick={() => setCurrentBlockIndex(prev => Math.max(0, prev - 1))}
                            disabled={currentBlockIndex === 0}
                            style={{
                                padding: '12px 25px',
                                background: currentBlockIndex === 0 ? '#f0f0f0' : '#4a90e2',
                                color: currentBlockIndex === 0 ? '#999' : 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: currentBlockIndex === 0 ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FiArrowLeft /> Предыдущий блок
                        </button>

                        {currentBlockIndex === testData.blocks.length - 1 ? (
                            <button
                                onClick={handleSubmitTest}
                                style={{
                                    padding: '12px 25px',
                                    background: '#52c41a',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '16px'
                                }}
                            >
                                Отправить тест
                            </button>
                        ) : (
                            <button
                                onClick={() => setCurrentBlockIndex(prev => Math.min(testData.blocks.length - 1, prev + 1))}
                                style={{
                                    padding: '12px 25px',
                                    background: '#4a90e2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                Следующий блок <FiArrowLeft style={{ transform: 'rotate(180deg)' }} />
                            </button>
                        )}
                    </div>
                </div>
            </main>
        );
    }

    // Детальные результаты теста
    if (showDetailedResults && detailedResults) {
        return (
            <main className="student-content">
                <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <button
                        onClick={() => {
                            setShowDetailedResults(false);
                            setDetailedResults(null);
                        }}
                        style={{
                            marginBottom: '20px',
                            padding: '10px 20px',
                            background: '#f0f0f0',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontWeight: '500'
                        }}
                    >
                        <FiArrowLeft /> Назад к тестам
                    </button>

                    <div className="card" style={{ padding: '30px' }}>
                        <h1 style={{ marginBottom: '10px' }}>Результаты тестирования</h1>
                        <h2 style={{ color: '#666', fontWeight: '400', marginBottom: '30px' }}>{detailedResults.test_title}</h2>

                        {detailedResults.blocks.map((block, blockIndex) => {
                            const totalQuestions = block.questions.length;
                            const correctQuestions = block.questions.filter(q => q.is_correct).length;
                            const incorrectQuestions = block.questions.filter(q => !q.is_correct && q.is_correct !== null).length;
                            const unansweredQuestions = block.questions.filter(q => !q.answered).length;

                            return (
                                <div key={block.id} style={{ marginBottom: '30px' }}>
                                    <h3 style={{ marginBottom: '15px' }}>{block.title}</h3>
                                    <div style={{ marginBottom: '10px', color: '#666' }}>
                                        Вы ответили верно на {correctQuestions} вопросов из {totalQuestions}
                                    </div>
                                    
                                    {/* Точки-индикаторы */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {block.questions.map((question, qIndex) => (
                                            <div
                                                key={qIndex}
                                                style={{
                                                    width: '30px',
                                                    height: '30px',
                                                    borderRadius: '50%',
                                                    background: question.is_correct === null 
                                                        ? '#d9d9d9' // Серый для письма
                                                        : question.is_correct 
                                                            ? '#52c41a' // Зеленый для правильных
                                                            : !question.answered
                                                                ? '#d9d9d9' // Серый для неотвеченных
                                                                : '#ff4d4f', // Красный для неправильных
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '12px',
                                                    fontWeight: '600'
                                                }}
                                                title={question.is_correct === null 
                                                    ? 'Письменное задание' 
                                                    : question.is_correct 
                                                        ? 'Правильно' 
                                                        : !question.answered
                                                            ? 'Не отвечено'
                                                            : 'Неправильно'}
                                            >
                                                {qIndex + 1}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </main>
        );
    }

    // Список тестов
    return (
        <main className="student-content">
            <div className="container">
                <h1 style={{ marginBottom: '25px' }}>Тестирование</h1>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>Загрузка...</div>
                ) : tests.length === 0 ? (
                    <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
                        <p style={{ fontSize: '18px', color: '#999' }}>Нет доступных тестов</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '15px' }}>
                        {tests.map(test => (
                            <div key={test.id} className="card test-blocks-card">
                                <div className="test-blocks-content">
                                    <div className="test-blocks-info">
                                        <div className="test-blocks-title-row">
                                            <h3>{test.title}</h3>
                                            <span 
                                                className="test-blocks-badge"
                                                style={{ background: getDifficultyColor(test.difficulty) }}
                                            >
                                                {test.difficulty}
                                            </span>
                                            {test.result_id && !test.can_take && (
                                                <>
                                                    {test.has_writing && !test.writing_checked ? (
                                                        <span className="test-blocks-badge checking">
                                                            <FiClock size={12} /> На проверке
                                                        </span>
                                                    ) : (
                                                        <span 
                                                            className="test-blocks-badge"
                                                            style={{
                                                                background: (test.writing_checked ? test.final_percentage : test.percentage) >= test.passing_score ? '#52c41a' : '#ff4d4f'
                                                            }}
                                                        >
                                                            {(test.writing_checked ? test.final_percentage : test.percentage) >= test.passing_score 
                                                                ? `Пройден (${Math.round(test.writing_checked ? test.final_percentage : test.percentage)}%)` 
                                                                : `Не пройден (${Math.round(test.writing_checked ? test.final_percentage : test.percentage)}%)`}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        {test.description && (
                                            <p style={{ margin: '10px 0', color: '#666' }}>{test.description}</p>
                                        )}
                                        <div className="test-blocks-meta">
                                            <span>
                                                <FiFileText size={14} /> {test.blocks_count || 0} блоков
                                            </span>
                                            {test.time_limit > 0 && (
                                                <span>
                                                    <FiClock size={14} /> {test.time_limit} мин
                                                </span>
                                            )}
                                            <span>
                                                <FiCheckCircle size={14} /> Проходной балл: {test.passing_score}%
                                            </span>
                                        </div>
                                    </div>
                                    {test.can_take ? (
                                        <button
                                            onClick={() => handleStartTest(test)}
                                            className="test-blocks-btn"
                                        >
                                            <FiPlay /> Начать тест
                                        </button>
                                    ) : test.result_id ? (
                                        <button
                                            onClick={() => handleViewDetailedResults(test.result_id)}
                                            className="test-blocks-btn view-results"
                                        >
                                            <FiFileText /> Посмотреть результат
                                        </button>
                                    ) : (
                                        <div style={{
                                            padding: '12px 25px',
                                            background: '#f0f0f0',
                                            color: '#999',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            textAlign: 'center'
                                        }}>
                                            Пройден
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
};

export default StudentTestsBlocksPage;
